import {CriticalityLevel} from "../tenant/tenant.service";
import {Network} from "../networks/network.service";
import {CustomField, CustomFieldFilterForm} from "../custom-fields/custom-field.model";
import {DataAccess} from "../data/data-access.model";
import {TenantAccount} from "../accounts/account.model";

export enum DataCollectionStatus {
	ACTIVE = "ACTIVE",
	OUTDATED = "OUTDATED",
	ERROR = "ERROR",
}

export enum DataCollector {
	KAPSUL = "KAPSUL",
	AGENT = "AGENT",
}

export function parseDataCollectionStatus(
	input: unknown
): DataCollectionStatus | undefined {
	if (typeof input !== "string") return undefined;

	const normalized = input.trim().toUpperCase();

	// Since this is a string enum, values are the strings above
	const values = Object.values(DataCollectionStatus) as string[];

	return values.includes(normalized)
		? (normalized as DataCollectionStatus)
		: undefined;
}

export interface ServerInterface {
	serverId: string,
	ipAddress: string,
	isPrimary: boolean,
	networkId: string | null
}

export interface ServerInterfaceNetwork {
	serverInterface: ServerInterface,
	network: Network | null
}

export interface IpAddress {
	value: string;
	type: string;
}

export interface ServerAgent {
	id: string,
	name: string | null,
	type: string,
	lastCheck: string | null,
	version: string,
	installDate: string,
	osType: string
}

export interface InfrastructureOverview {
	allApplicationUsage: { applicationId: string, applicationName: string, usage: LoadMetric[]}[];
	metrics: Metric;
	averageMetrics: MetricAverage;
}

export interface MetricAverage {
	load: number | null;
	storage: number | null;
}

export interface  Metric {
	cpuList: LoadMetric[];
	memoryList: LoadMetric[];
}

export interface LoadMetric {
	timestamp: string,
	load: number | null
}

export interface StorageMetric {
	timestamp: string,
	disks: DiskMetric[]
}

export interface DiskMetric {
	name: string,
	load: number,
}

export interface ServerForm {
	name: string;
	os: string;
	ipAddress: string;
	manufacturer?: string;
}


export interface ServerTableData {
	server: Server,
	customFields: CustomField[],
	criticality: CriticalityLevel | null,
	load: number | null,
	storage: number | null,
	owners?: TenantAccount[] | null,
	cpu?: number | null,
	memory?: number | null,
	storageTotal?: number | null,
}

export interface Server {
	id: string,
	name: string,
	description: string | null,
	automatic: boolean,
	system: string,
	os: string,
	model: string | null,
	serialNumber: string | null,
	uptime: number | null,
	manufacturer: string | null,
	interfaces: ServerInterfaceNetwork[];
	type: string | null,
	location: string | null,
	dataCollectionStatus: DataCollectionStatus | null,
	lastCheckTime: string | null,
	cpuModel?: string,
	cpuCount?: number,
	cpuCoreCount?: number,
	memorySize?: number,
	disks?: Disk[],
	domain?: string,
	fqdn?: string
	linkedApplications?: DataAccess<LinkedApplication[]>
}

export interface LinkedApplication {
	id: string,
	name: string | null,
	iconUrl: string | null,
}

export interface Disk {
	id: string,
	size?: number,
	freeSpace?: number,
}

/** Index of each tab of the server detail drawer (order of the tabs in the template). */
export enum ServerDetailTab {
	OVERVIEW = 0,
	OPERATIONS = 1,
	NETWORK = 2,
	INSTALLED_PROGRAMS = 3
}

export enum ServerType {
	DATABASE = 'database',
	PRINT = 'print',
	DIRECTORY = 'directory',
	PROXY = 'proxy',
	SECURITY = 'security',
	APPLICATION = 'application',
	WEB = 'web',
	MONITORING = 'monitoring',
	MAIL = 'mail',
	BACKUP = 'backup',
	CONFIGURATION = 'configuration',
	FILE = 'file'
}

export enum ServerOS {
	WINDOWS = 'windows',
	SUSE = 'suse',
	REDHAT = 'redhat',
	UBUNTU = 'ubuntu',
	DEBIAN = 'debian',
	CENTOS = 'centos',
	FEDORA = 'fedora',
	IBMI = 'ibmi',
	OTHER = 'other'
}

export enum NetworkRole {
	LOCAL = 'local',
	WIRELESS = 'wireless',
	STORAGE = 'storage',
	VPN = 'vpn',
	DATA_CENTER = 'data_center',
	EDGE = 'edge',
	PUBLIC = 'public',
	MANAGEMENT = 'management',
	DMZ = 'dmz',
	IOT = 'iot',
	INTERCOMMUNICATION = 'intercommunication',
}

/** Filter value of a network without a role (a detected one until a user sets it); cannot clash with a real role. */
export const NETWORK_ROLE_NONE = '__none__';

/** The value a network is filtered on: its role, or {@link NETWORK_ROLE_NONE}. */
export function networkRoleFilterValue(role: NetworkRole | null | undefined): string {
	return role ?? NETWORK_ROLE_NONE;
}

/** Translation key of a network role filter value. */
export function networkRoleLabelKey(value: string): string {
	return value === NETWORK_ROLE_NONE ? 'page.infrastructure.network.noRole' : `page.infrastructure.network.roles.${value.toLowerCase()}`;
}

export enum SubnetMask {
	SUB_1 = '/1',
	SUB_2 = '/2',
	SUB_3 = '/3',
	SUB_4 = '/4',
	SUB_5 = '/5',
	SUB_6 = '/6',
	SUB_7 = '/7',
	SUB_8 = '/8',
	SUB_9 = '/9',
	SUB_10 = '/10',
	SUB_11 = '/11',
	SUB_12 = '/12',
	SUB_13 = '/13',
	SUB_14 = '/14',
	SUB_15 = '/15',
	SUB_16 = '/16',
	SUB_17 = '/17',
	SUB_18 = '/18',
	SUB_19 = '/19',
	SUB_20 = '/20',
	SUB_21 = '/21',
	SUB_22 = '/22',
	SUB_23 = '/23',
	SUB_24 = '/24',
	SUB_25 = '/25',
	SUB_26 = '/26',
	SUB_27 = '/27',
	SUB_28 = '/28',
	SUB_29 = '/29',
	SUB_30 = '/30',
	SUB_31 = '/31',
	/** A single host: what cloud VMs report (GCP gives each VM a /32). */
	SUB_32 = '/32',
}

export interface ServerInformationForm {
	name: string,
	ipAddress?: string,
	location?: string,
	type?: ServerType,
	os?: ServerOS,
	manufacturer?: string
}

export interface UpdateServerDescriptionForm {
	description?: string
}

export interface SchemaServer {
	server: Server,
	current: boolean,
	criticality: CriticalityLevel | null,
}

export interface ApplicationServerListForm {
	servers: string[];
}

export interface ServerInterfaceNetworkForm {
	previousIpAddress: string;
	ipAddress: string,
	isPrimary: boolean,
	networkId: string | null
}

export interface ServerInterfaceNetworkDeleteForm {
	ipAddress: string,
}

export interface ServerTableFilterForm {
	name?: string | null;
	location?: string[] | null;
	type?: ServerType[] | null;
	criticality?: CriticalityLevel[] | null;
	os?: ServerOS[] | null;
	manufacturer?: string[] | null;
	application?: string[] | null;
	customFields?: CustomFieldFilterForm[];
	noValueFilters?: string[];
}

// Request interface for backend filtering and pagination
export interface ServerTableFilterRequest {
	name?: string | null;
	type?: string[] | null;
	location?: string[] | null;
	criticality?: string[] | null;
	os?: string[] | null;
	manufacturer?: string[] | null;
	tag?: string[] | null;
	application?: string[] | null;
	customFields?: ServerCustomFieldFilterRequest[] | null;
	noValueFilters?: string[] | null;
	sortDirection?: string | null;
	sortColumn?: string | null;
	limit?: number | null;
	offset?: number | null;
	visibleColumns?: string[] | null;
}

export interface ServerCustomFieldFilterRequest {
	fieldId: string;
	type: string;
	values?: string[] | null;
	dateFilter?: ServerDateFilterRequest | null;
}

export interface ServerDateFilterRequest {
	beforeAfter: string;
	date?: string | null;
}

// Request interface for server export - accepts either IDs or filters
export interface ServerExportRequest {
	serverIds?: string[] | null;
	name?: string | null;
	type?: string[] | null;
	location?: string[] | null;
	criticality?: string[] | null;
	os?: string[] | null;
	manufacturer?: string[] | null;
	tag?: string[] | null;
	application?: string[] | null;
	customFields?: ServerCustomFieldFilterRequest[] | null;
	noValueFilters?: string[] | null;
}

// Response interface for paginated server data
export interface ServerTableDataResponse {
	servers: ServerTableData[];
	total: number;
}

// Export interfaces
export interface ServerExportData {
	server: Server;
	applications: {
		accessible: boolean;
		data: { id: string; name: string; iconUrl: string | null }[] | null;
		restriction: { missingPermission: string; count: number; message: string | null } | null;
	};
	customFields: CustomField[];
	criticality: string | null;
	load: number | null;
	storage: number | null;
	agentVersion: string | null;
}

export interface ServerExportCsv {
	serverId: string;
	serverName: string;
	description: string;
	os: string;
	type: string;
	location: string;
	mainIpAddress: string;
	applicationIds: string;
	applicationNames: string;
	criticality: string;
	load: string;
	storage: string;
	dataCollectionStatus: string;
	lastCollectDate: string;
	agentVersion: string;
	[key: string]: string; // For dynamic custom field columns
}

// ---------- Server network flows (cartography) ----------

export type FlowHealth = 'good' | 'warning' | 'critical' | 'unknown';

/** A single connection (flow) between two machines — shown in the hover tooltip. */
export interface ServerFlowConnection {
	protocol?: string;   // TCP / UDP
	port?: number;       // e.g. 443
	service?: string;    // e.g. HTTPS, SSH
	process?: string;    // executable driving the flow, e.g. "nginx", "sshd"
	volume?: string;     // human-readable volume exchanged, e.g. "≈ 3,6 MB"
	icon?: string;       // optional icon override (else inferred from service/port)
}

/** One endpoint of a server's network flows (source on the left, target on the right). */
export interface ServerFlowEndpoint {
	id: string;
	label: string;
	subLabel?: string;
	/** Renders the grey "?" card (external / unresolved IP) instead of a server icon. */
	unknown?: boolean;
	/** Server type id (e.g. "web", "database") — drives the icon. Ignored when `unknown`. */
	serverType?: string;
	/** Operational health — colours the ring around the icon. */
	health?: FlowHealth;
	/** Data-collection status — drives the small dot next to the name (same colours as the server table). */
	collectionStatus?: DataCollectionStatus | null;
	/** Shows the small orange obsolescence badge at the bottom-right of the icon. */
	obsolescence?: boolean;
	/** Traffic level: 1 = baseline, higher = more/faster particles along the edge. */
	intensity?: number;
	/** The individual flows running between this endpoint and the central server. */
	connections?: ServerFlowConnection[];
}

/** Flux réseaux of a single server: who talks to it (sources) and where it talks (targets). */
export interface ServerFlowData {
	center: ServerFlowEndpoint;
	sources: ServerFlowEndpoint[];
	targets: ServerFlowEndpoint[];
}
